
import Page from '@/Page.vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import moment from 'moment';
import {
    LocationModel,
    PlanItemModel,
    QueryOperator,
    ShiftModel,
    ShiftProductionRecordModel,
    ShiftScrapRecordModel,
    SortOrder,
    WorkplaceModel,
} from '@/libs/Api';
import { workplacesStore } from '@/libs/workplaces/+state/store';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { locationsStore } from '@/libs/locations/+state/store';
import { shiftScrapRecordsStore, shiftsStore } from '@/libs/shifts/+state/store';
import { CrudAction } from '@/libs/core/+state/models/crud-action';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import EditShiftDetailDialog from '@/libs/shifts/components/EditShiftDetailDialog.vue';
import { planItemsStore } from '@/libs/plan-items/+state/store';
import ShiftDetailDialog from '@/libs/shifts/components/ShiftDetailDialog.vue';

@Component({
    components: { ShiftDetailDialog, EditShiftDetailDialog },
})
export default class ShiftReportLocation extends Page {
    @Prop() locationId!: string | null;

    protected date: Date = new Date();
    protected dateString = '';
    protected activeShiftId: string | null = null;

    protected daysBefore = 6;
    protected daysAfter = 0;

    protected menu = false;

    protected headers = [{ text: this.$t('common.workplace'), value: 'name', sortable: false, align: 'left' }];

    get isLoading(): boolean {
        return (
            shiftsStore.useGetter(CrudGetter.Pending, 'ShiftReportLocation') ||
            shiftScrapRecordsStore.useGetter(CrudGetter.Pending, 'ShiftReportLocation') ||
            planItemsStore.useGetter(CrudGetter.Pending, 'ShiftReportLocation')
        );
    }

    get hours(): {
        fromTime: number | null;
        fromTimeString: string | null;
        toTime: number | null;
        toTimeString: string | null;
        lines: {
            workplace: WorkplaceModel;
            okCount: number;
            nokCount: number;
            plan: number;
            shutdownDots: number;
            shiftId: string | null;
        }[];
        plan: number;
        okCount: number;
        nokCount: number;
        isFinal: boolean;
    }[] {
        let fromTime = moment(this.date).startOf('day').add(6, 'hour').toDate();
        const toTime = moment(fromTime).add(1, 'day').toDate();
        const items: {
            fromTime: number | null;
            fromTimeString: string | null;
            toTime: number | null;
            toTimeString: string | null;
            lines: {
                workplace: WorkplaceModel;
                okCount: number;
                nokCount: number;
                plan: number;
                shutdownDots: number;
                shiftId: string | null;
            }[];
            plan: number;
            okCount: number;
            nokCount: number;
            isFinal: boolean;
        }[] = [];
        const shifts: ShiftModel[] = this.getShifts();
        const transformedShifts = shifts.map((e) => {
            return {
                id: e.id,
                fromTime: new Date(e.fromDate!).getTime(),
                toTime: new Date(e.toDate!).getTime(),
                hourlyPlan: e.hourlyPlan ?? 0,
                workplaceId: e.workplaceId!,
            };
        });
        const shutdowns = ((planItemsStore.useGetter(CrudGetter.Data, 'ShiftReportLocation') ?? []) as PlanItemModel[])
            .map((x) => {
                return {
                    fromTime: x.shutdownFromDate ? new Date(x.shutdownFromDate!).getTime() : new Date().getTime(),
                    toTime: x.shutdownToDate ? new Date(x.shutdownToDate!).getTime() : new Date().getTime(),
                    workplaceId: x.workplaceId!,
                };
            })
            .filter((x) => x.fromTime < x.toTime)
            .sort((a, b) => a.fromTime - b.fromTime) // Sort shutdowns by fromTime
            .reduce((acc, current) => {
                if (acc.filter((x) => x.workplaceId == current.workplaceId).length === 0) {
                    // If no shutdown in the accumulator, add the current one
                    acc.push(current);
                } else {
                    // Get the last shutdown in the accumulator
                    const last = acc.filter((x) => x.workplaceId == current.workplaceId)[
                        acc.filter((x) => x.workplaceId == current.workplaceId).length - 1
                    ];

                    // Check if the current shutdown overlaps or is adjacent to the last one
                    if (current.fromTime <= last.toTime) {
                        // Merge the two shutdowns by extending the toTime of the last shutdown
                        last.toTime = Math.max(last.toTime, current.toTime);
                    } else {
                        // No overlap, add the current shutdown to the accumulator
                        acc.push(current);
                    }
                }
                return acc;
            }, [] as { fromTime: number; toTime: number; workplaceId: string }[]);
        console.log(shutdowns);
        const scrapRecords: ShiftScrapRecordModel[] = this.getScrapRecords();
        const workplaceRecords = shifts.reduce(
            (acc, e) => {
                const productions = (e.shiftProductions ?? [])
                    .reduce((acc, n) => {
                        return [...acc, ...(n.shiftProductionRecords ?? [])];
                    }, [] as ShiftProductionRecordModel[])
                    .map((n: ShiftProductionRecordModel) => {
                        return {
                            recordTime: new Date(n.recordTime!).getTime(),
                            okCount: n.okCount ?? 0,
                        };
                    });
                if (Object.keys(acc).contains(e.workplaceId!)) {
                    acc[e.workplaceId!] = [...acc[e.workplaceId!], ...productions];
                } else {
                    acc[e.workplaceId!] = productions;
                }
                return acc;
            },
            {} as {
                [key: string]: { recordTime: number; okCount: number }[];
            }
        );
        const workplaceScrapRecords = shifts.reduce(
            (acc, e) => {
                const records = scrapRecords
                    .filter((x) => x.shiftId == e.id)
                    .map((n: ShiftScrapRecordModel) => {
                        return {
                            recordTime: new Date(n.recordTime!).getTime(),
                            scrapCount: n.scrapCount ?? 0,
                        };
                    });
                if (records.length == 0) {
                    return acc;
                }
                if (Object.keys(acc).contains(e.workplaceId!)) {
                    acc[e.workplaceId!] = [...acc[e.workplaceId!], ...records];
                } else {
                    acc[e.workplaceId!] = records;
                }
                return acc;
            },
            {} as {
                [key: string]: { recordTime: number; scrapCount: number }[];
            }
        );
        const workplaces = this.getWorkplaces();
        while (fromTime.getTime() < toTime.getTime()) {
            const fromTimeNumber = fromTime.getTime();
            const toTimeNumber = fromTime.getTime() + 3600000;
            const lines = workplaces.map((e) => {
                const records = workplaceRecords[e.id!] ?? [];
                const scrapRecords = workplaceScrapRecords[e.id!] ?? [];
                const lineShifts = transformedShifts.filter(
                    (x) => x.workplaceId == e.id! && x.fromTime < toTimeNumber && x.toTime > fromTimeNumber
                );
                return {
                    workplace: e,
                    okCount: records
                        .filter((x) => x.recordTime > fromTimeNumber && x.recordTime <= toTimeNumber)
                        .reduce((acc, e) => acc + e.okCount, 0),
                    nokCount: scrapRecords
                        .filter((x) => x.recordTime > fromTimeNumber && x.recordTime <= toTimeNumber)
                        .reduce((acc, e) => acc + e.scrapCount, 0),
                    plan: lineShifts
                        .map((x) => {
                            const pFromTime = fromTimeNumber > x.fromTime ? fromTimeNumber : x.fromTime;
                            const pToTime = toTimeNumber < x.toTime ? toTimeNumber : x.toTime;
                            if (pFromTime < pToTime) {
                                return Math.round((x.hourlyPlan ?? 0) * ((pToTime - pFromTime) / 3600000));
                            }
                            return 0;
                        })
                        .reduce((acc, x) => acc + x, 0),
                    shutdownDots: Math.ceil(
                        shutdowns
                            .filter((x) => x.workplaceId == e.id! && x.fromTime < toTimeNumber && x.toTime > fromTimeNumber)
                            .map((x) => {
                                const pFromTime = fromTimeNumber > x.fromTime ? fromTimeNumber : x.fromTime;
                                const pToTime = toTimeNumber < x.toTime ? toTimeNumber : x.toTime;
                                if (pFromTime < pToTime) {
                                    return pToTime - pFromTime;
                                }
                                return 0;
                            })
                            .reduce((acc, x) => acc + x / 600000, 0)
                    ),
                    shiftId: lineShifts.length > 0 ? lineShifts[0].id! : null,
                };
            });
            items.push({
                fromTime: fromTimeNumber,
                fromTimeString: fromTime.toISOString(),
                toTime: toTimeNumber,
                toTimeString: new Date(toTimeNumber).toISOString(),
                lines,
                plan: lines.reduce((acc, e) => acc + e.plan, 0),
                okCount: lines.reduce((acc, e) => acc + e.okCount, 0),
                nokCount: lines.reduce((acc, e) => acc + e.nokCount, 0),
                isFinal: false,
            });
            fromTime = moment(fromTime).add(1, 'hour').toDate();
        }
        const totalLines = workplaces.map((e) => {
            const records = workplaceRecords[e.id!] ?? [];
            const scrapRecords = workplaceScrapRecords[e.id!] ?? [];
            return {
                workplace: e,
                okCount: records.reduce((acc, e) => acc + e.okCount, 0),
                nokCount: scrapRecords.reduce((acc, e) => acc + e.scrapCount, 0),
                plan: shifts.filter((x) => x.workplaceId == e.id).reduce((acc, e) => acc + (e.planCount ?? 0), 0),
                shutdownDots: 0,
                shiftId: null,
            };
        });
        items.push({
            fromTime: null,
            fromTimeString: null,
            toTime: null,
            toTimeString: null,
            lines: totalLines,
            plan: totalLines.reduce((acc, e) => acc + e.plan, 0),
            okCount: totalLines.reduce((acc, e) => acc + e.okCount, 0),
            nokCount: totalLines.reduce((acc, e) => acc + e.nokCount, 0),
            isFinal: true,
        });
        return items;
    }

    @Watch('locationId')
    locationIdChanged(v: string | null, p: string | null) {
        if (v != p) {
            this.loadShifts();
            this.loadScrapRecords();
            this.loadShutdowns();
        }
    }

    @Watch('dateString')
    dateStringChanged(v: string) {
        if (moment(v).isValid()) {
            this.setDate(moment(v).toDate());
        }
    }

    created() {
        if (this.getQueryInt('date', true)) {
            this.date = new Date(this.getQueryInt('date', true)!);
        } else {
            if (moment().hour() >= 0 && moment().hour() < 6) {
                this.date = moment().subtract(1, 'day').startOf('day').add(6, 'hour').toDate();
            }
        }
    }

    mounted() {
        if (this.locationId) {
            this.loadShifts();
            this.loadScrapRecords();
            this.loadShutdowns();
        }
    }

    getWorkplaces(): WorkplaceModel[] {
        const locationScopes = this.getLocationScopes();
        let workplaces: WorkplaceModel[] = workplacesStore.useGetter(CrudGetter.Data) ?? [];
        workplaces = workplaces
            .filter((x) => locationScopes.includes(x.locationId!))
            .sort((a, b) => (a.name ?? '').localeCompare((b.name ?? '').toString()));
        return workplaces;
    }

    getShifts(): ShiftModel[] {
        return shiftsStore.useGetter(CrudGetter.Data, 'ShiftReportLocation') ?? [];
    }

    getScrapRecords(): ShiftModel[] {
        return shiftScrapRecordsStore.useGetter(CrudGetter.Data, 'ShiftReportLocation') ?? [];
    }

    getLocationScopes() {
        const locations: LocationModel[] = locationsStore.useGetter(CrudGetter.Data) ?? [];
        return locations
            .filter((x) => x.locationParents!.some((y) => y.parentalLocationId! == this.locationId))
            .map((x) => x.id!)
            .concat([this.locationId ?? '']);
    }

    getDate(dayShift: number) {
        return moment(new Date()).startOf('day').add(dayShift, 'days').toDate();
    }

    getStartOfDay(date: Date) {
        return moment(date).startOf('day').toDate();
    }

    isDate(e: Date, d: Date) {
        return e.toDateString() === d.toDateString();
    }

    setDate(e: Date) {
        this.date = e;
        this.setQuery('date', e.getTime().toString(), true, 'Q', moment().startOf('day').add(6, 'hour').add(1, 'day').toDate());
        this.loadShifts();
        this.loadScrapRecords();
        this.loadShutdowns();
    }

    loadShifts() {
        shiftsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload(
                { field: 'fromDate', index: 1, order: SortOrder.Asc },
                [
                    {
                        field: 'fromDate',
                        op: QueryOperator.Lt,
                        comparand: moment(this.date).endOf('day').add(6, 'hour').toDate(),
                    },
                    {
                        field: 'toDate',
                        op: QueryOperator.Gt,
                        comparand: moment(this.date).startOf('day').add(6, 'hour').toDate(),
                    },
                    {
                        field: 'workplace.locationId',
                        op: QueryOperator.In,
                        comparand: this.getLocationScopes(),
                    },
                ],
                {
                    pageIndex: 1,
                    pageSize: 99999,
                },
                false,
                'ShiftReportLocation'
            )
        );
    }

    loadScrapRecords() {
        shiftScrapRecordsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload(
                { field: 'id', index: 1, order: SortOrder.Asc },
                [
                    {
                        field: 'shift.fromDate',
                        op: QueryOperator.Lt,
                        comparand: moment(this.date).endOf('day').add(6, 'hour').toDate(),
                    },
                    {
                        field: 'shift.toDate',
                        op: QueryOperator.Gt,
                        comparand: moment(this.date).startOf('day').add(6, 'hour').toDate(),
                    },
                    {
                        field: 'shift.workplace.locationId',
                        op: QueryOperator.In,
                        comparand: this.getLocationScopes(),
                    },
                ],
                {
                    pageIndex: 1,
                    pageSize: 99999,
                },
                false,
                'ShiftReportLocation'
            )
        );
    }

    loadShutdowns() {
        const fromDate = moment(this.date).startOf('day').add(6, 'hour').toISOString();
        const toDate = moment(this.date).endOf('day').add(6, 'hour').toISOString();
        planItemsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload<PlanItemModel>(
                { field: 'time', index: 1, order: 'asc' },
                [
                    {
                        field: 'IsWorkplaceShutdown',
                        op: 'eq',
                        comparand: true,
                    },
                    {
                        field: 'id',
                        op: 'wrap',
                        comparand: [
                            {
                                field: 'id',
                                op: 'wrap',
                                comparand: [
                                    {
                                        field: 'shutdownToDate',
                                        op: 'gt',
                                        comparand: fromDate,
                                    },
                                    {
                                        field: 'shutdownFromDate',
                                        op: 'lt',
                                        comparand: toDate,
                                    },
                                ],
                                junction: 'or',
                            },
                            {
                                field: 'id',
                                op: 'wrap',
                                comparand: [
                                    {
                                        field: 'shutdownFromDate',
                                        op: 'lt',
                                        comparand: toDate,
                                    },
                                    {
                                        field: 'shutdownToDate',
                                        op: 'eq',
                                        comparand: null,
                                    },
                                ],
                                junction: 'or',
                            },
                        ],
                    },
                    {
                        field: 'workplace.locationId',
                        op: QueryOperator.In,
                        comparand: this.getLocationScopes(),
                    },
                ],
                undefined,
                false,
                'ShiftReportLocation'
            )
        );
    }

    openWorkplaceShiftClick(shiftId: string) {
        this.activeShiftId = shiftId;
    }
}
